import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bCustomerStatementAPI from 'api/b2b/CustomerStatement'
import B2bTDPAPI from 'api/b2b/TdpApi'
import { downloadExcel } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import CustomerStatementList from './CustomerStatementList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

interface FormData {
  companyName: CompanyOption | null
  paymentMode: string
  orderStartDate: string
  orderEndDate: string
}

export type CustomerStatementTableDataProps = {
  company_name: string
  order_id: string
  product: string
  currency: string
  order_amount: number
  paid_amount: number
  pending_amount: number
  order_date: string
  payment_mode: string
  order_status: string
}

export default function CustomerStatementSearchForm() {
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [tableData, setTableData] = useState<CustomerStatementTableDataProps[]>(
    []
  )

  const debouncedSearchTerm = useDebounce(companySearchTerm, 500) // 500ms debounce

  const { control, watch, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      paymentMode: '',
      orderStartDate: '',
      orderEndDate: ''
    }
  })
  const companyNameInput = watch('companyName')

  // Debounce search based on query
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2, // Only search when user types at least 2 characters
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  // Mutation for customer statement search
  const {
    mutate: searchCustomerStatement
    // isLoading: statementLoading
    // error: statementFetchError,
  } = useMutation({
    mutationFn: async (params: Record<string, string>) => {
      const token = OrbitSession.get('faberToken')
      return await B2bCustomerStatementAPI.getCustomerStatement(token, params)
    },
    onSuccess: (data) => {
      if (data?.data?.length === 0 || !data?.data) {
        toast.info('No data found')
        return
      }
      setTableData(data?.data)
    },
    onError: () => {
      // console.error('Failed to fetch customer statement:', error)
      toast.error('Failed to fetch customer statement')
    }
  })

  const paymentModes = ['Credit Card', 'Bank Transfer', 'Cash']

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  // const filterOptions = useCallback((options: CompanyOption[]) => {
  //   return options
  // }, [])

  const onSubmit = (data: FormData) => {
    // Build search parameters object
    const params: Record<string, string> = {}

    params.company_id = data.companyName?.companyId || ''

    if (data.paymentMode) {
      params.payment_mode = data.paymentMode
    }

    if (data.orderStartDate) {
      params.order_start_date = data.orderStartDate
    }

    if (data.orderEndDate) {
      params.order_end_data = data.orderEndDate
    }
    // Trigger the mutation
    searchCustomerStatement(params)
  }

  const excelDownload = (data: CustomerStatementTableDataProps[]) => {
    const excelData = {
      fileName: companySearchTerm + '-CustomerStatement',
      CustomerStatement: data
    }
    downloadExcel(excelData)
  }

  const handleReset = () => {
    reset()
    setTableData([])
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          mb: 3
        }}
      >
        <Controller
          name='companyName'
          control={control}
          rules={{
            required: { value: true, message: 'Company name is required' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              options={companyOptions}
              // filterOptions={filterOptions}
              loading={isTenantLoading}
              getOptionLabel={(option: CompanyOption | null) =>
                option?.label || ''
              }
              isOptionEqualToValue={(
                option: CompanyOption,
                value: CompanyOption | null
              ) => option?.value === value?.value}
              onChange={(_, data: CompanyOption | null) => field.onChange(data)}
              onInputChange={handleCompanyInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Company Name *'
                  variant='outlined'
                  fullWidth
                  error={!!error}
                  helperText={
                    error?.message ||
                    (debouncedSearchTerm.length > 0 &&
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : '')
                  }
                  placeholder='Type company name to search...'
                  InputProps={{
                    ...params.InputProps
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Box>
                    <Typography variant='body1'>{option.label}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      ID: {option.companyId}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                debouncedSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : isTenantLoading
                  ? 'Searching...'
                  : tenantFetchError
                  ? 'Error loading companies'
                  : 'No companies found'
              }
            />
          )}
        />

        <Controller
          name='paymentMode'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label='Payment Mode'
              variant='outlined'
              fullWidth
            >
              {paymentModes.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {mode}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name='orderStartDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order Start Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        <Controller
          name='orderEndDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order End Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2 }}>
          <Button variant='outlined' color='secondary' onClick={handleReset}>
            Reset
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={!companyNameInput?.value}
          >
            Search
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={() => excelDownload(tableData)}
            disabled={!tableData?.length}
          >
            Excel Download
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box>
        <CustomerStatementList data={tableData} />
      </Box>
    </Box>
  )
}
